import { sortBy, isEmpty } from 'lodash-es';
import { FileSchema, LinkSchema, MarshalledResource, PreviewType, CarouselItem } from '@adobe/edex/ui/types';
import { MIME_TYPE_TEXT_REGEX } from '@adobe/edex/ui/shared/constants';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { getTranslation } from '@adobe/edex/ui/shared/config/i18n';
import { getExtension } from '@adobe/edex/ui/shared/utils';
import { mime2previewType, getFileIconUrl } from '@adobe/edex/ui/utils';
import { groupResourceLinks, mergeResourceLinks } from '@adobe/edex/ui/utils/resource-links';

/***
 * https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
 */
function formatBytes(bytes: number, decimals = 0) {
  if (!bytes) {
    return null;
  }
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function extension2typeDisplay(extension: string): string {
  switch (extension) {
    case 'pdf':
      return 'PDF';
    case 'doc':
    case 'docx':
      return 'Word';
    case 'xls':
    case 'xlsx':
      return 'Excel';
    case 'ppt':
    case 'pptx':
      return 'PowerPoint';
    case 'one':
      return 'OneNote';
    case 'odt':
      return 'Text';
    case 'odp':
      return 'Presentation';
    case 'ods':
      return 'Spreadsheet';
    case 'eps':
    case 'gif':
    case 'jpeg':
    case 'jpg':
    case 'png':
    case 'raw':
    case 'svg':
    case 'tiff':
    case 'bmp':
      return 'Image';
    default:
      return null;
  }
}

function mime2typeDisplay(mimeType: string): string {
  let result: string;
  if (MIME_TYPE_TEXT_REGEX.test(mimeType)) {
    result = 'Text';
  }
  return result;
}

export function getTypeDisplay(file: FileSchema) {
  const extension = getExtension(file.storagePath);
  if (mime2previewType(file.mimeType) === PreviewType.image) {
    return getTranslation(I18nKey.resourceImageType, {
      filetype: extension.toUpperCase(),
    });
  }
  return (
    extension2typeDisplay(extension) ||
    mime2typeDisplay(file.mimeType) ||
    getTranslation(I18nKey.resourceFileType, {
      filetype: extension.toUpperCase(),
    })
  );
}

export const getCanonicalImage = (link: LinkSchema) => link && link.metadata && link.metadata.image;

export const getCanonicalURL = (link: LinkSchema) =>
  link && ((link.metadata && (link.metadata.video || link.metadata.canonicalURL)) || link.url);

export const getCanonicalTitle = (link: LinkSchema) =>
  link && (link.description || (link.metadata && link.metadata.title) || link.url);

export const formatPreviewItems = (resource: MarshalledResource): CarouselItem[] => {
  const { web, video, images } = groupResourceLinks(mergeResourceLinks(resource.links));

  const webImages: CarouselItem[] = images.map((link) => ({
    id: link.id,
    type: PreviewType.image,
    url: getCanonicalURL(link),
    title: getCanonicalTitle(link),
    typeDisplay: getTranslation(I18nKey.resourceLinkWeb),
    icon: getFileIconUrl({
      storagePath: link.url,
      mimeType: link && link.metadata && link.metadata.contentType,
    }),
    sortOrder: link.sortOrder,
    components: link.components,
  }));

  const videoLinks: CarouselItem[] = video.map((link) => ({
    id: link.id,
    type: PreviewType.video,
    url: getCanonicalURL(link),
    title: getCanonicalTitle(link),
    typeDisplay: getTranslation(I18nKey.resourceLinkVideo),
    image: getCanonicalImage(link),
    sortOrder: link.sortOrder,
    components: link.components,
    remixable: link.metadata?.remixable,
    isProjectXRemixLink: link.metadata?.isProjectXRemixLink,
    largeThumbnail: link.metadata?.largeThumbnail,
  }));

  const webLinks: CarouselItem[] = web.map((link) => ({
    id: link.id,
    type: PreviewType.web,
    url: getCanonicalURL(link),
    title: getCanonicalTitle(link),
    typeDisplay: getTranslation(I18nKey.resourceLinkWeb),
    image: getCanonicalImage(link),
    sortOrder: link.sortOrder,
    urn: link.metadata?.urn,
    assetType: link.metadata?.ccxAssetType,
    isLinkFromAssetBrowser: Boolean(link.metadata?.isLinkFromAssetBrowser),
    ownerUserId: link.metadata?.ownerUserId,
    components: link.components,
    branchURL: link.metadata?.branchURL,
    hasMetadata: link.metadata && !isEmpty(link.metadata) ? true : false,
    remixable: link.metadata?.remixable,
    isProjectXRemixLink: link.metadata?.isProjectXRemixLink,
    isLinkFromProjectXAssetBrowser: Boolean(link.metadata?.isLinkFromProjectXAssetBrowser),
    gneissAlias: link.metadata?.gneissAlias,
    largeThumbnail: link.metadata?.largeThumbnail,
  }));

  const files: CarouselItem[] = resource.files.map((file) => ({
    file,
    id: file.id,
    type: mime2previewType(file.mimeType),
    url: file.storagePath,
    title: file.label,
    typeDisplay: getTypeDisplay(file),
    size: formatBytes(+file.size),
    icon: getFileIconUrl(file),
    sortOrder: file.sortOrder,
    components: file.components,
  }));

  const items = [...files, ...webImages, ...webLinks, ...videoLinks];

  const canSort = items.every(({ sortOrder }) => sortOrder !== undefined);
  return canSort ? sortBy(items, 'sortOrder') : items;
};

export function getGoogleViewerURL(url: string) {
  url = url.replace('https://dev', 'https://stage'); // temp fix: 3-rd party iframe doesn't have access to dev cdn
  return `https://docs.google.com/viewerng/viewer?embedded=true&url=${url}`;
}
